import _ from "lodash";
import React, { useState, useRef, useMemo } from "react";
import classes from "./ChatAndChanneDualPannel.scss";
import { SearchInput, EmptyState } from "@toddle-edu/ds-web";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withLoader from "UIComponents/GqlWithLoader";
import PaginationLoader from "UIComponents/PaginationLoader";
import { prepareLoadMoreFeed } from "Utils";
import { getChannelListQuery } from "MessagingV2/components/ThreadList/Utils/ThreadListQueries";
import { getChannelListFromCache } from "MessagingV2/components/ThreadList/Utils/ThreadListGraphqlHelpers";
import {
  getChannelListQueryVariables,
  FORWARD_MESSAGE_VISIBILITY_CONFIG,
  CHAT_STATUS_ENUM,
  isPaidConnectModule,
  isInitiateMessageEnabled,
  getFormattedNameForUserType,
  CHANNEL_TYPES_LIST,
  isMessagingV2ChannelEnabled,
} from "MessagingV2/modules/Utils";
import EntityCohorts from "./EntityCohorts";
import { useHandleScroll } from "Utils/CustomHooks";
import RightContainer from "./RightContainer";
import { useI18n } from "Hooks";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { getMessageV2UsersWithStudentParentQuery } from "MessagingV2/modules/MessagingQuery";
import { getMessageV2UsersWithStudentParentFromCache } from "MessagingV2/modules/MessagingGraphqlHelper";
import { getOrganizationId, getUserType } from "modules/CommonSelectors";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const COHORTS = [
  FORWARD_MESSAGE_VISIBILITY_CONFIG.CHANNELS,
  FORWARD_MESSAGE_VISIBILITY_CONFIG.USERS,
];

const addCustomFamilyChannelToChannels = ({
  membersParams,
  channelListParams,
  currentUserInfo,
}) => {
  const isChannelEnabled = isMessagingV2ChannelEnabled();
  if (!isChannelEnabled) {
    return {
      updatedChannelListParams: channelListParams,
    };
  }

  const membersEdges = _.get(membersParams, "edges", []);
  const channelEdges = _.get(channelListParams, "edges", []);

  const students = _.filter(
    membersEdges,
    ({ node }) =>
      _.isEqual(node.type, "student") && _.gt(_.size(node.familyMembers), 0)
  );

  const customFamilyChannels = _.map(students, ({ node }) => {
    const label = `Family of ${getFormattedNameForUserType({
      user: node,
    })} with ${getFormattedNameForUserType({ user: currentUserInfo })}`;
    return {
      __typename: "ChatEdge",
      node: {
        id: `NEW_${node.id}`,
        entityId: node.id,
        label,
        totalUsersCount: _.size(node.familyMembers),
        type: "CUSTOM_FAMILY_CHANNEL",
      },
    };
  });

  return {
    updatedChannelListParams: {
      ...channelListParams,
      edges: [...channelEdges, ...customFamilyChannels],
    },
  };
};

const EmptySearch = ({ searchText }) => {
  const { t } = useI18n(["common"]);
  return (
    <div className={classes.emptyStateContainer}>
      <div className={classes.emptyState}>
        <EmptyState
          illustration={NoSearchInFilterIllustration}
          title={searchText ? t("common:no_result_found") : ""}
          subtitle={
            searchText
              ? t("common:no_results_with_label", {
                  label: searchText,
                })
              : t("common:forward_message_to_chat_and_channels")
          }
        />
      </div>
    </div>
  );
};
const getVisibilityConfig = params => {
  const {
    channelListParams,
    searchText,
    membersParams,
    hideUsersList,
  } = params;

  if (
    _.isEmpty(_.trim(searchText)) ||
    (_.isEmpty(channelListParams.edges) && _.isEmpty(membersParams.edges))
  ) {
    return FORWARD_MESSAGE_VISIBILITY_CONFIG.EMPTY;
  }

  if (_.get(channelListParams, "hasNextPage", false) || hideUsersList) {
    return FORWARD_MESSAGE_VISIBILITY_CONFIG.CHANNELS;
  }

  return FORWARD_MESSAGE_VISIBILITY_CONFIG.USERS;
};

const LeftContaienrWrapper = props => {
  const {
    membersParams,
    channelListParams = {},
    selectedEntity,
    onEntitySelect,
    searchText,
    hideUsersList,
  } = props;

  const { t } = useI18n(["common"]);
  const userInfo = getUserInfo();
  const { updatedChannelListParams } = useMemo(() => {
    return addCustomFamilyChannelToChannels({
      t,
      membersParams,
      channelListParams,
      currentUserInfo: {
        firstName: userInfo.first_name,
        lastName: userInfo.last_name,
      },
    });
  }, [userInfo, membersParams, channelListParams]);

  const cohortsParams = {
    membersParams,
    channelListParams: updatedChannelListParams,
  };

  const visibilityConfig = getVisibilityConfig({
    membersParams,
    channelListParams,
    searchText,
    hideUsersList,
  });

  const { hasNextPage, networkStatus, isFetchingMore, loadMoreChats } = _.get(
    cohortsParams,
    visibilityConfig.dataKey,
    {}
  );

  const containerRef = useRef(null);
  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreChats,
    hasNextPage,
    getScrollRef: () => containerRef?.current,
  });

  if (
    _.isEqual(
      visibilityConfig.priority,
      FORWARD_MESSAGE_VISIBILITY_CONFIG.EMPTY.priority
    )
  ) {
    return <EmptySearch searchText={searchText} />;
  }

  return (
    <div className={classes.entityCohortsContainer} ref={containerRef}>
      {_.map(COHORTS, data => {
        const { key, priority, dataKey, header } = data;
        return _.lte(priority, visibilityConfig.priority) ? (
          <EntityCohorts
            entityKey={key}
            entityData={_.get(cohortsParams, `${dataKey}.edges`, [])}
            header={header}
            key={key}
            selectedEntity={selectedEntity}
            onEntitySelect={onEntitySelect}
          />
        ) : null;
      })}
      {isFetchingMore ? <PaginationLoader /> : null}
    </div>
  );
};

const mapStateToProps = state => {
  const userType = getUserType();
  return {
    isData: true,
    isLoading: false,
    userType,
  };
};

const ContainerWrapperWithLoader = compose(
  connect(mapStateToProps, null),
  graphql(getChannelListQuery, {
    name: "getChannelList",
    skip: () => !isMessagingV2ChannelEnabled(),
    options: ({ searchText, userType }) => {
      const type = _.isEqual(userType, "staff") ? CHANNEL_TYPES_LIST : [];
      return {
        fetchPolicy: "cache-and-network",
        variables: getChannelListQueryVariables({
          input: {
            first: 10,
            search: {
              text: searchText,
            },
            type,
          },
        }),
      };
    },
    props({ getChannelList, ownProps: { isData, isLoading } }) {
      const { variables } = getChannelList;
      const chatResult = getChannelListFromCache({ variables });
      const edges = _.get(chatResult, "node.chats.edges", []);
      const pageInfo = _.get(chatResult, "node.chats.pageInfo", {});
      const hasNextPage = _.get(
        chatResult,
        "node.chats.pageInfo.hasNextPage",
        false
      );
      const networkStatus = _.get(getChannelList, "networkStatus");

      const isFetchingMore = networkStatus === 3;

      const channelListParams = {
        edges,
        hasNextPage,
        networkStatus,
        isFetchingMore,
        loadMoreChats: prepareLoadMoreFeed({
          variables: {
            ...variables,
            input: {
              ...variables.input,
              after: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getChannelList,
          key: "chats",
        }),
      };
      return {
        isData: !_.isEmpty(chatResult) && isData,
        isLoading:
          _.includes([1, 2], getChannelList["networkStatus"]) || isLoading,
        channelListParams,
      };
    },
  }),

  graphql(getMessageV2UsersWithStudentParentQuery, {
    name: "getMessageV2UsersWithStudentParent",
    options: props => {
      const { searchText, personalChatReceiverUserId } = props;
      const excludedUserIds = personalChatReceiverUserId
        ? [personalChatReceiverUserId]
        : [];
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          portalType: "PLANNER",
          input: {
            first: 10,
            userType: [STAFF, STUDENT, PARENT],
            searchText,
            userStatus: "ACTIVE",
            chatStatus: isInitiateMessageEnabled()
              ? CHAT_STATUS_ENUM.ALL
              : CHAT_STATUS_ENUM.INITIATED,
            includeInitiatedChatUsersOnlyForType:
              !_.isEqual(getUserType(), PARENT) && !isPaidConnectModule()
                ? [STAFF, STUDENT]
                : [],
            includePaidUsersOnly: _.isEqual(getUserType(), PARENT)
              ? false
              : isPaidConnectModule(),
            includeFreeAndInitiatedUsersAlsoOfType: [PARENT],
            includeInitiatedChatUsers: false,
            excludedUserIds,
          },
        },
      };
    },
    props: ({
      getMessageV2UsersWithStudentParent,
      ownProps: { isData, isLoading },
    }) => {
      const queryVariables = _.get(
        getMessageV2UsersWithStudentParent,
        "variables",
        {}
      );
      const cachedData = getMessageV2UsersWithStudentParentFromCache(
        queryVariables
      );

      const userNodeList = _.get(cachedData, "messageV2Users.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "messageV2Users.pageInfo.hasNextPage",
        false
      );

      const membersParams = {
        edges: userNodeList,
        hasNextPage,
        networkStatus: getMessageV2UsersWithStudentParent["networkStatus"],
        isFetchingMore: _.includes(
          [3],
          getMessageV2UsersWithStudentParent["networkStatus"]
        ),

        loadMoreChats: prepareLoadMoreFeed({
          query: getMessageV2UsersWithStudentParentQuery,
          variables: {
            ...queryVariables,
            input: {
              ..._.get(queryVariables, "input", {}),
              before: _.get(cachedData, "messageV2Users.pageInfo.endCursor"),
            },
          },
          queryData: getMessageV2UsersWithStudentParent,
          key: "messageV2Users",
        }),
      };

      return {
        isData: !_.isEmpty(cachedData) && isData,
        isLoading: _.includes(
          [1, 2],
          getMessageV2UsersWithStudentParent["networkStatus"] || isLoading
        ),
        membersParams,
      };
    },
  }),
  withLoader
)(LeftContaienrWrapper);

const ChatAndChannelDualPannel = props => {
  const {
    selectedEntity,
    setSelectedEntity,
    entityDetails,
    setEntityDetails,
    hideUsersList = false,
    personalChatReceiverUserId,
  } = props;
  const [searchText, setSearchText] = useState("");

  const { t } = useI18n(["common"]);
  const onEntitySelect = ({ type, id, isChecked, entity }) => {
    setSelectedEntity(prevState => {
      const entitySet = new Set(prevState[type]);
      _.forEach(id, data => {
        if (isChecked) {
          entitySet.delete(data);
        } else {
          entitySet.add(data);
        }
      });

      return {
        ...prevState,
        [type]: entitySet,
      };
    });

    setEntityDetails(prevState => {
      const rightPaneEntity = { ..._.get(prevState, type, {}) };
      _.forEach(id, data => {
        if (isChecked) {
          //do above for right pane data also
          _.unset(rightPaneEntity, data);
        } else {
          rightPaneEntity[data] = entity;
        }
      });

      return {
        ...prevState,
        [type]: rightPaneEntity,
      };
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.leftContainer}>
        <div className={classes.searchBar}>
          <SearchInput
            size="large"
            onChange={setSearchText}
            value={searchText}
            placeholder={t(
              "common:search_bar_placeholder_select_channel_members"
            )}
          />
        </div>
        {searchText ? (
          <ContainerWrapperWithLoader
            searchText={searchText}
            selectedEntity={selectedEntity}
            onEntitySelect={onEntitySelect}
            hideUsersList={hideUsersList}
            personalChatReceiverUserId={personalChatReceiverUserId}
          />
        ) : (
          <EmptySearch />
        )}
      </div>
      <div className={classes.rightContaienr}>
        <RightContainer
          entityDetails={entityDetails}
          onExclude={onEntitySelect}
        />
      </div>
    </div>
  );
};

export default ChatAndChannelDualPannel;
